import './EditTablePresetModal.scss';
import cn from 'classnames';
import * as R from 'ramda';
import {
  ChangeEvent,
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { LagOption } from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { Form, Modal } from 'react-bootstrap';
import { MdClose, MdEdit } from 'react-icons/md';
import * as uuid from 'uuid';
import {
  DeleteCustomMetricParams,
  SaveCustomMetric,
  SaveCustomMetricBody,
  SaveCustomMetricResponse
} from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { GlobalOptions } from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  Column,
  DimensionColumn,
  MetricsTablePreset,
  SortInfo,
  SuperHeader as PresetSuperHeader
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { CustomMetric } from '@blisspointmedia/bpm-types/dist/pgTables/CustomMetric';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { AdminLockToggle } from '@Components/AdminLockToggle/AdminLockToggle';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { ColumnMetaData, ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import * as UserRedux from '@src/redux/user';
import { useAppSelector } from '@src/redux';
import { getChannelSeriesColor } from '@src/utils/colors';
import {
  DeleteColumn,
  getInvalidGranularityDetailsBySource,
  HeaderInfo,
  MoveColumn
} from '@src/utils/columnMetricSystem/configUtils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { awaitJSON, SingleChannelLambdaFetch } from '@src/utils/fetch-utils';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import { CustomMetricsModal } from '@Components/MetricsTable/CustomMetricsModal/CustomMetricsModal';
import { GroupedColumnPicker } from '@Pages/CrossChannel/EditTablePresetModal';
import { ConfigColumn } from './ConfigColumn';
import { ConfigDimensionColumn } from './ConfigDimensionColumn';
import { SortingBox } from './SortingBox';
import { SuperHeader } from './SuperHeader';

interface EditTablePresetModalProps {
  columnMetaDataMap: ColumnMetaDataMap;
  // The company's default window, for the column `7d (Default)` label.
  defaultLagWindow?: LagOption;
  defaultPresetNames?: string[];
  dimensionColumnMetaDataMap: ColumnMetaDataMap;
  globalOptions?: GlobalOptions;
  kpiMetaData: Record<string, KpiInfo>;
  onHide?: () => void;
  prefix: SingleChannelMediaTypes | 'cross_channel' | 'pacing';
  presetChanges: MetricsTablePreset;
  saveAndApplyOnClick: (tablePresetChanges: MetricsTablePreset) => void;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  refreshCustomMetrics: () => Promise<void> | void;
  analyticsIsSource?: boolean;
  groupedSegments?: Record<string, GroupedSegment[]>;
  // the page's active filter dims; each picker checks them together with the rest of the table
  filterDims?: string[];
  // Forwarded to each ConfigColumn (see GroupedColumnPicker).
  groupedColumnPicker?: GroupedColumnPicker;
}

export const EDIT_TABLE_PRESET_MODAL_CLASSNAME = 'edit-table-preset-modal';

// NOTE: change the constants in the CSS if you change these
const CONFIG_COLUMN_WIDTH = 270 as const;
const CONFIG_COLUMN_DIVIDER_WIDTH = 10 as const;
const CONFIG_COLUMN_GUTTER = 16 as const;

const EMPTY_DIMS: string[] = [];

export const EditTablePresetModal = ({
  columnMetaDataMap,
  defaultLagWindow,
  defaultPresetNames = [],
  dimensionColumnMetaDataMap = {},
  globalOptions = { kpi: '', isDefault: false },
  kpiMetaData,
  onHide = () => {},
  prefix,
  presetChanges,
  refreshCustomMetrics,
  saveAndApplyOnClick,
  setPresetChanges,
  analyticsIsSource,
  groupedSegments,
  filterDims = EMPTY_DIMS,
  groupedColumnPicker
}: EditTablePresetModalProps) => {
  const [colorMap, setColorMap] = useState<Record<string, string | undefined>>({});
  const [currentCustomMetric, setCurrentCustomMetric] = useState<CustomMetric>();
  const [customMetricToSet, setCustomMetricToSet] = useState<string | undefined>();
  const [hoverItem, setHoverItem] = useState<string>();
  const [newName, setNewName] = useState<string | undefined>();
  const [cmColumnIndex, setCMColumnIndex] = useState<number | undefined>();
  const [customMetricLoading, setCustomMetricLoading] = useState(false);
  const [showCustomMetricsModal, setShowCustomMetricsModal] = useState(false);
  const [showRenameCustomMetricsModal, setShowRenameCustomMetricsModal] = useState(false);
  const adminLock = Boolean(presetChanges?.admin_lock);
  const internalOnly = Boolean(presetChanges?.role && presetChanges.role < 2);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();
  const shouldEnableMetricsTableSegmentGroups = Boolean(
    useExperimentFlag('enableMetricsTableSegmentGroups')
  );

  useEffect(() => {
    setColorMap(currentColorMap => {
      const newColorMap = { ...currentColorMap };
      let count = Object.keys(newColorMap).length;
      for (const cols of [presetChanges.dimensionColumns, presetChanges.dataColumns]) {
        for (const col of cols) {
          if (!newColorMap[col.id]) {
            newColorMap[col.id] = getChannelSeriesColor(count);
            count += 1;
          }
        }
      }
      return newColorMap;
    });
  }, [presetChanges]);

  const moveColumn = useCallback<MoveColumn>(
    (sourceId, destinationId) => {
      let sourceI = -1;
      let destinationI = -1;
      for (let i = 0; i < presetChanges.dataColumns.length; i += 1) {
        const col = presetChanges.dataColumns[i];
        if (col.id === sourceId) {
          sourceI = i;
        }
        if (col.id === destinationId) {
          destinationI = i;
        }
      }
      if (sourceI === -1 || destinationI === -1) return;
      const reordered = R.move(sourceI, destinationI, presetChanges.dataColumns);
      let dataIndex = 0;
      setPresetChanges(current => ({
        ...current,
        dataColumns: reordered,
        superHeaders: R.reduce(
          (headers, header) => {
            // If it's a one-column-wide super header and that column is the one we're moving, delete
            // the header
            const start = dataIndex;
            const end = dataIndex + header.span - 1;
            if (start === end && start === sourceI) {
              return headers;
            }
            // S = Source, D = Destination, Res = Result
            // U = Under, B = Before, A = After
            const sU = start <= sourceI && end >= sourceI;
            const dU = start <= destinationI && end >= destinationI;
            const sB = start > sourceI;
            const dB = start > destinationI;
            const sA = end < sourceI;
            const dA = end < destinationI;
            if ((sU && dU) || (sB && dB) || (sA && dA)) {
              dataIndex = end + 1;
              return [...headers, header];
            }
            const newHeader = { ...header };
            if (sA) {
              newHeader.span += 1;
            }
            if (sB) {
              newHeader.span -= 1;
            }
            if (dB) {
              newHeader.span += 1;
            }
            if (dA) {
              newHeader.span -= 1;
            }
            dataIndex = end + 1;
            return [...headers, newHeader];
          },
          [] as PresetSuperHeader[],
          current.superHeaders
        )
      }));
    },
    [presetChanges, setPresetChanges]
  );

  const deleteColumn = useCallback<DeleteColumn>(
    async (index: number) => {
      try {
        await setAreYouSure({
          title: 'Delete Column?',
          message: 'Are you sure you want to delete this column?',
          okayText: 'Delete',
          cancelText: 'Never mind'
        });

        setPresetChanges(current => {
          const dataColumns = current.dataColumns.filter((_col, i) => i !== index);
          let defaultSorting: SortInfo[] | undefined;
          if (current.defaultSorting) {
            const { id } = current.dataColumns[index];
            const newDefaultSorting = current.defaultSorting.filter(sortCol => sortCol.id !== id);
            defaultSorting = newDefaultSorting.length ? newDefaultSorting : undefined;
          }
          let columnIndex = 0;
          const superHeaders = (current.superHeaders ?? []).map(superHeader => {
            const start = columnIndex;
            const end = columnIndex + superHeader.span - 1;
            const newSuperHeader =
              index >= start && index <= end
                ? { ...superHeader, span: superHeader.span - 1 }
                : superHeader;
            columnIndex = end + 1;
            return newSuperHeader;
          });
          return { ...current, dataColumns, defaultSorting, superHeaders };
        });
      } catch {
        // Cancelled — nothing to do.
      }
    },
    [setAreYouSure, setPresetChanges]
  );

  const addColumn = useCallback(
    (i: number) => {
      const dataVarName = Object.keys(columnMetaDataMap).find(key => columnMetaDataMap[key]);
      if (!dataVarName) return;
      let dataColumnIndex = 0;
      setPresetChanges(current => ({
        ...current,
        dataColumns: R.insert(
          i,
          { id: uuid.v4(), dataVarName, role: 10 } as Column,
          current.dataColumns
        ),
        superHeaders: (current.superHeaders ?? []).map(superHeader => {
          const start = dataColumnIndex;
          const end = dataColumnIndex + superHeader.span - 1;
          if (i >= start && i <= end) {
            return { ...superHeader, span: superHeader.span + 1 };
          }
          dataColumnIndex = end + 1;
          return superHeader;
        })
      }));
    },
    [setPresetChanges, columnMetaDataMap]
  );

  const superHeaderInfoMap = useMemo(() => {
    const map: Record<string, HeaderInfo | true> = {};
    let columnIndex = 0;
    if (presetChanges.superHeaders) {
      for (let superHeaderIndex = 0; superHeaderIndex < presetChanges.superHeaders.length;) {
        const superHeader = presetChanges.superHeaders[superHeaderIndex];
        const start = columnIndex;
        const end = columnIndex + superHeader.span - 1;
        if (superHeader.data.text) {
          // Take gutter out so half gutter on either side is removed
          let width = -CONFIG_COLUMN_GUTTER;
          for (let i = start; i <= end; i += 1) {
            map[i] = true;
            width += CONFIG_COLUMN_WIDTH + CONFIG_COLUMN_GUTTER;
            if (presetChanges.dataColumns[i]?.divider) {
              width += CONFIG_COLUMN_DIVIDER_WIDTH;
            }
          }
          map[start] = { end, start, superHeaderIndex, text: superHeader.data.text, width };
        }
        columnIndex = end + 1;
        superHeaderIndex += 1;
      }
    }
    return map;
  }, [presetChanges]);

  const onSuperHeaderChange = useCallback(
    (newSuperHeaders: PresetSuperHeader[]) => {
      // Clean up the super headers to collapse blanks
      const cleanedSuperHeaders = newSuperHeaders.reduce<PresetSuperHeader[]>((acc, current) => {
        if (current.data.text === '') {
          if (acc.length === 0 || acc[acc.length - 1].data.text !== '') {
            return [...acc, current];
          }
          const last = acc[acc.length - 1];
          return [...acc.slice(0, -1), { ...last, span: last.span + current.span }];
        }
        return [...acc, current];
      }, []);
      setPresetChanges(current => ({ ...current, superHeaders: cleanedSuperHeaders }));
    },
    [setPresetChanges]
  );

  const deleteDimensionColumn = useCallback<DeleteColumn>(
    async (index: number) => {
      try {
        await setAreYouSure({
          title: 'Delete Column?',
          message: 'Are you sure you want to delete this column?',
          okayText: 'Delete',
          cancelText: 'Never mind'
        });
        setPresetChanges(current => {
          const dimensionColumns = current.dimensionColumns.filter((_col, i) => {
            if (i === index) return false;
            if (
              groupedSegments &&
              shouldEnableMetricsTableSegmentGroups &&
              Object.keys(groupedSegments).includes(
                current.dimensionColumns[index].dimensionVarName
              )
            ) {
              return !groupedSegments[current.dimensionColumns[index].dimensionVarName]?.some(
                segment => segment.segmentName === current.dimensionColumns[i].dimensionVarName
              );
            }
            return true;
          });
          let defaultSorting: SortInfo[] | undefined;
          if (current.defaultSorting) {
            const { id } = current.dimensionColumns[index];
            const newDefaultSorting = current.defaultSorting.filter(sortCol => sortCol.id !== id);
            defaultSorting = newDefaultSorting.length ? newDefaultSorting : undefined;
          }
          return { ...current, dimensionColumns, defaultSorting };
        });
      } catch {
        // Cancelled — nothing to do.
      }
    },
    [setAreYouSure, setPresetChanges, groupedSegments, shouldEnableMetricsTableSegmentGroups]
  );

  const moveDimensionColumn = useCallback<MoveColumn>(
    (sourceId, destinationId) => {
      let sourceI = -1;
      let destinationI = -1;
      for (let i = 0; i < presetChanges.dimensionColumns.length; i += 1) {
        const col = presetChanges.dimensionColumns[i];
        if (col.id === sourceId) {
          sourceI = i;
        }
        if (col.id === destinationId) {
          destinationI = i;
        }
      }
      if (sourceI === -1 || destinationI === -1) return;
      const reordered = R.move(sourceI, destinationI, presetChanges.dimensionColumns);
      setPresetChanges(current => ({ ...current, dimensionColumns: reordered }));
    },
    [presetChanges, setPresetChanges]
  );

  const addDimensionColumn = useCallback(
    (i: number) => {
      const dimensionTypeName = Object.keys(dimensionColumnMetaDataMap).find(
        key => dimensionColumnMetaDataMap[key]
      );
      const dimensionVarName = dimensionTypeName
        ? (dimensionColumnMetaDataMap[dimensionTypeName] as ColumnMetaData).name
        : undefined;
      if (!dimensionTypeName || !dimensionVarName) return;
      setPresetChanges(current => ({
        ...current,
        dimensionColumns: R.insert(
          i,
          { dimensionTypeName, dimensionVarName, id: uuid.v4(), role: 10 } as DimensionColumn,
          current.dimensionColumns
        )
      }));
    },
    [dimensionColumnMetaDataMap, setPresetChanges]
  );

  const addSuperHeader = useCallback(
    (columnI: number) => {
      const newSuperHeaderData = {
        text: 'New Super Header',
        leftDivider: false,
        rightDivider: false
      };
      const superHeaders: PresetSuperHeader[] = [];
      let dataColumnIndex = 0;
      let inserted = false;
      for (const superHeader of presetChanges.superHeaders ?? []) {
        const start = dataColumnIndex;
        const end = dataColumnIndex + superHeader.span - 1;
        if (start > columnI || end < columnI) {
          superHeaders.push(superHeader);
        } else if (start === end && start === columnI) {
          superHeaders.push({ ...superHeader, data: newSuperHeaderData });
          inserted = true;
        } else if (start === columnI) {
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          superHeaders.push({ ...superHeader, span: superHeader.span - 1 });
          inserted = true;
        } else if (end === columnI) {
          superHeaders.push({ ...superHeader, span: superHeader.span - 1 });
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          inserted = true;
        } else {
          const leftSpan = columnI - start;
          superHeaders.push({ ...superHeader, span: leftSpan });
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          superHeaders.push({ ...superHeader, span: superHeader.span - leftSpan - 1 });
          inserted = true;
        }
        dataColumnIndex = end + 1;
      }
      if (!inserted) {
        if (columnI > dataColumnIndex) {
          superHeaders.push({
            data: { leftDivider: false, rightDivider: false, text: '' },
            span: columnI - dataColumnIndex
          });
        }
        superHeaders.push({ data: newSuperHeaderData, span: 1 });
      }
      onSuperHeaderChange(superHeaders);
    },
    [onSuperHeaderChange, presetChanges.superHeaders]
  );

  const hasSuperHeaderMap = useMemo(() => {
    const hasSuperHeaderList: boolean[] = [];
    let colIndex = 0;
    for (const superHeader of presetChanges.superHeaders ?? []) {
      for (let i = 0; i < superHeader.span; i += 1) {
        if (superHeader.data.text) {
          hasSuperHeaderList[colIndex] = true;
        }
        colIndex += 1;
      }
    }
    return hasSuperHeaderList;
  }, [presetChanges]);

  const onCustomMetricsModalHide = useCallback(() => {
    setShowCustomMetricsModal(false);
    setShowRenameCustomMetricsModal(false);
    setCurrentCustomMetric(undefined);
  }, []);

  const deleteCustomMetric = useCallback(
    (customMetric: CustomMetric) => {
      (async () => {
        try {
          setCustomMetricLoading(true);
          const res = await SingleChannelLambdaFetch<DeleteCustomMetricParams>(
            '/deleteCustomMetric',
            { method: 'DELETE', params: { id: customMetric.id } }
          );
          await awaitJSON<SaveCustomMetricResponse>(res);
          await refreshCustomMetrics();
          setError({
            title: 'Success',
            intent: Intent.SUCCESS,
            message: `Deleted custom metric: ${customMetric.display_name}!`
          });
        } catch (e) {
          setError({
            message: `Failed to delete custom metric. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setCustomMetricLoading(false);
        }
      })();
    },
    [refreshCustomMetrics, setError]
  );

  const saveCustomMetric = useCallback(
    (customMetric: Partial<CustomMetric>) => {
      (async () => {
        try {
          setCustomMetricLoading(true);
          const res = await SingleChannelLambdaFetch<SaveCustomMetricBody>('/saveCustomMetric', {
            method: 'POST',
            body: { customMetric: customMetric as SaveCustomMetric }
          });
          const result = await awaitJSON<SaveCustomMetricResponse>(res);
          await refreshCustomMetrics();
          setError({
            title: 'Success',
            intent: Intent.SUCCESS,
            message: `Saved custom metric: ${result.display_name}!`
          });
          if (customMetric.id == null && cmColumnIndex != null) {
            setCustomMetricToSet(customMetric.name);
          }
        } catch (e) {
          setError({
            message: `Failed to save custom metric. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setCustomMetricLoading(false);
          onCustomMetricsModalHide();
        }
      })();
    },
    [cmColumnIndex, onCustomMetricsModalHide, refreshCustomMetrics, setError]
  );

  // Once a newly-saved custom metric's ColumnMetaData entry lands in `columnMetaDataMap`
  // (after `refreshCustomMetrics` triggers a refetch upstream), auto-select it on the
  // column that opened the "Add New Custom Metric" flow.
  useEffect(() => {
    if (cmColumnIndex == null || !customMetricToSet) return;
    const meta = columnMetaDataMap[customMetricToSet];
    if (!meta) return;
    setPresetChanges(changes => ({
      ...changes,
      dataColumns: changes.dataColumns.map((col, colIndex) =>
        colIndex === cmColumnIndex
          ? {
              ...col,
              dataVarName: customMetricToSet,
              requiredColumns: meta.requiredColumns ? [...meta.requiredColumns] : undefined,
              customColumnID: meta.customMetaData?.id
            }
          : col
      )
    }));
    setCMColumnIndex(undefined);
    setCustomMetricToSet(undefined);
  }, [cmColumnIndex, columnMetaDataMap, customMetricToSet, setPresetChanges]);

  const modalHeader = (
    <Modal.Header>
      <div className='title-container'>
        <Modal.Title>Table Preset:</Modal.Title>
        <div className='table-preset-name-container'>
          {newName !== undefined ? (
            <Form.Control
              className='new-name-input'
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              placeholder='New Preset Name'
              value={newName}
            />
          ) : (
            <div className='table-preset-name'>
              {presetChanges.temporary ? 'Temporary Preset' : presetChanges.name}
            </div>
          )}
          {presetChanges?.name &&
            !defaultPresetNames.includes(presetChanges.name.toLowerCase()) &&
            (newName === undefined ? (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='edit-table-preset-name-icon'
                onClick={() => setNewName(presetChanges.name)}
              >
                <MdEdit />
              </IconButton>
            ) : (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='cancel-rename-icon'
                onClick={() => setNewName(undefined)}
              >
                <MdClose />
              </IconButton>
            ))}
        </div>
      </div>
      <AdminLockToggle
        checked={adminLock}
        onChange={() => setPresetChanges(current => ({ ...current, admin_lock: !adminLock }))}
        className='internal-only-toggle'
        disabled={!isAdmin}
      />
      <ToggleSwitch
        checked={internalOnly}
        className='internal-only-toggle'
        design={Design.BRAND}
        label='Internal-only'
        onChange={() => setPresetChanges(current => ({ ...current, role: internalOnly ? 10 : 1 }))}
      />
      <IconButton
        appearance={Appearance.EMPTY}
        className='close-button'
        onClick={() => onHide()}
        size={Size.MD}
      >
        <MdClose />
      </IconButton>
    </Modal.Header>
  );

  // Keyed by column id. A picker replaces its own column, so each one checks the filter dims
  // together with every other table col.
  const dimensionPickerGranularityDetails = useMemo(() => {
    const dimOptions = Object.values(dimensionColumnMetaDataMap).map(col => col?.name ?? '');
    const dataCols = presetChanges.dataColumns.map(col => col.dataVarName);
    return Object.fromEntries(
      presetChanges.dimensionColumns.map(column => [
        column.id,
        getInvalidGranularityDetailsBySource(
          filterDims,
          [
            ...presetChanges.dimensionColumns
              .filter(col => col.id !== column.id)
              .map(info => info.dimensionVarName),
            // selected data cols can also be granularity-restricted (e.g. campaign-only
            // impression_share), so they constrain which dimensions are valid too
            ...dataCols
          ],
          dimOptions,
          prefix
        )
      ])
    );
  }, [dimensionColumnMetaDataMap, filterDims, prefix, presetChanges]);

  const dataPickerGranularityDetails = useMemo(() => {
    const dataOptions = Object.keys(columnMetaDataMap);
    const dimCols = presetChanges.dimensionColumns.map(info => info.dimensionVarName);
    return Object.fromEntries(
      presetChanges.dataColumns.map(column => [
        column.id,
        getInvalidGranularityDetailsBySource(
          filterDims,
          [
            ...dimCols,
            ...presetChanges.dataColumns
              .filter(col => col.id !== column.id)
              .map(col => col.dataVarName)
          ],
          dataOptions,
          prefix
        )
      ])
    );
  }, [columnMetaDataMap, filterDims, prefix, presetChanges]);

  const modalBody = Object.keys(columnMetaDataMap).length > 0 && (
    <Modal.Body>
      <div className='config-box'>
        <SortingBox
          colorMap={colorMap}
          presetChanges={presetChanges}
          setHoverItem={setHoverItem}
          setPresetChanges={setPresetChanges}
        />
        <div
          className={cn('config-box-inner', {
            'has-headers':
              presetChanges.superHeaders &&
              ((presetChanges.superHeaders[0] && presetChanges.superHeaders[0].data.text) ||
                presetChanges.superHeaders.length > 1)
          })}
        >
          {presetChanges.dimensionColumns
            .filter(
              column =>
                !(
                  groupedSegments &&
                  shouldEnableMetricsTableSegmentGroups &&
                  Object.values(groupedSegments)
                    .flatMap(group => Object.values(group))
                    .some(segment => segment.segmentName === column.dimensionTypeName)
                )
            )
            .map((column, i) => (
              <ConfigDimensionColumn
                key={column.id}
                addColumn={addDimensionColumn}
                color={colorMap[column.id]}
                column={column}
                columnMetaDataMap={dimensionColumnMetaDataMap}
                deleteColumn={deleteDimensionColumn}
                dimensionColumns={presetChanges.dimensionColumns}
                groupedSegments={groupedSegments}
                hoverItem={hoverItem}
                index={i}
                isOnlyColumn={presetChanges.dimensionColumns.length <= 1}
                moveColumn={moveDimensionColumn}
                analyticsIsSource={analyticsIsSource}
                prefix={prefix}
                setPresetChanges={setPresetChanges}
                invalidGranularityDetails={dimensionPickerGranularityDetails[column.id]?.table}
                incompatibleTableDimsFromFilter={
                  dimensionPickerGranularityDetails[column.id]?.filter
                }
              />
            ))}
          <div className='divider' />
          {presetChanges.dataColumns.map((column, i) => (
            <Fragment key={column.id}>
              <SuperHeader
                colCount={presetChanges.dataColumns.length}
                columnI={i}
                info={superHeaderInfoMap}
                onSuperHeaderChange={onSuperHeaderChange}
                superHeaders={presetChanges.superHeaders ?? []}
              />
              <ConfigColumn
                defaultLagWindow={defaultLagWindow}
                addColumn={addColumn}
                addCustomMetric={() => {
                  setShowCustomMetricsModal(true);
                  setCMColumnIndex(i);
                }}
                color={colorMap[column.id]}
                column={column}
                columnMetaDataMap={columnMetaDataMap}
                deleteColumn={deleteColumn}
                deleteCustomMetric={deleteCustomMetric}
                duplicateCustomMetric={(customMetric: SaveCustomMetric) =>
                  saveCustomMetric({
                    ...customMetric,
                    display_name: `${customMetric.display_name} (Copy)`,
                    name: `${customMetric.name}_copy`,
                    id: undefined
                  })
                }
                editCustomMetric={(customMetric: CustomMetric) => {
                  setShowCustomMetricsModal(true);
                  setCurrentCustomMetric(customMetric);
                }}
                globalKpi={globalOptions.kpi}
                groupedColumnPicker={groupedColumnPicker}
                hasSuperHeader={hasSuperHeaderMap[i]}
                hoverItem={hoverItem}
                index={i}
                isOnlyColumn={presetChanges.dataColumns.length <= 1}
                kpiMetaData={kpiMetaData}
                moveColumn={moveColumn}
                onSuperHeaderAdd={() => addSuperHeader(i)}
                prefix={prefix}
                renameCustomMetric={customMetric => {
                  setCurrentCustomMetric(customMetric);
                  setShowRenameCustomMetricsModal(true);
                }}
                setPresetChanges={setPresetChanges}
                invalidGranularityDetails={dataPickerGranularityDetails[column.id]?.table}
                incompatibleTableDataColsFromFilter={
                  dataPickerGranularityDetails[column.id]?.filter
                }
              />
            </Fragment>
          ))}
        </div>
      </div>
    </Modal.Body>
  );

  const modalFooter = (
    <Modal.Footer>
      <Button
        size={Size.MD}
        className='cancel-button'
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        onClick={() => onHide()}
      >
        Cancel
      </Button>
      <Button
        size={Size.MD}
        className='preview-button'
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        onClick={() =>
          saveAndApplyOnClick({
            ...presetChanges,
            temporary: true,
            id: (presetChanges.temporary ? presetChanges.id : undefined) as MetricsTablePreset['id']
          })
        }
      >
        Temp Preview
      </Button>
      <Button
        size={Size.MD}
        className='save-and-apply-button'
        disabled={adminLock && !isAdmin}
        design={Design.BRAND}
        onClick={() => {
          // Save and Apply always persists a permanent preset — saving a temp view converts it.
          const newPreset = {
            ...presetChanges,
            name: newName ?? presetChanges.name,
            temporary: false
          };
          saveAndApplyOnClick(newPreset);
          setPresetChanges(newPreset);
        }}
      >
        Save and Apply
      </Button>
    </Modal.Footer>
  );

  return (
    <>
      <Modal
        show
        centered
        size='lg'
        onHide={() => onHide()}
        className={cn(EDIT_TABLE_PRESET_MODAL_CLASSNAME, { disabled: customMetricLoading })}
      >
        {modalHeader}
        {modalBody}
        {modalFooter}
      </Modal>
      {showCustomMetricsModal && !customMetricLoading && (
        <CustomMetricsModal
          columnMetaDataMap={columnMetaDataMap}
          customMetric={currentCustomMetric}
          disabled={customMetricLoading}
          kpiMetaData={kpiMetaData}
          onHide={onCustomMetricsModalHide}
          prefix={prefix}
          save={saveCustomMetric}
          showDecimals
        />
      )}
      {showRenameCustomMetricsModal && !customMetricLoading && (
        <RenameModal
          applyOnClick={(newDisplayName: string) => {
            if (!currentCustomMetric) return;
            saveCustomMetric({
              ...currentCustomMetric,
              // A rename changes `display_name` only; `name` is the metric's permanent id.
              display_name: newDisplayName
            });
          }}
          applyText='Save'
          cancelText='Cancel'
          disabled={customMetricLoading}
          onCancel={onCustomMetricsModalHide}
          onHide={onCustomMetricsModalHide}
          placeholder='Rename custom metric'
          subTitle={`Rename ${currentCustomMetric?.display_name}`}
          title='Rename Custom Metric'
        />
      )}
    </>
  );
};
